import { authSelectors } from 'ducks/auth'
import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import ListResultNotFound from 'elements/list/ListResultNotFound'
import { CostSource } from 'pages/ordering/type'
import React, { useMemo } from 'react'
import { DatagridLoading } from 'react-admin'
import { useSelector } from 'react-redux'
import type { OpenSolarThemeType } from 'Themes'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { RootState } from 'types/state'
import InverterAutoListHeader from '../inverterWizard/components/InverterAutoListHeader'
import type { InverterWizardRecordType } from '../inverterWizard/types'
import { HardwareComponentType, HardwareListRowProps } from '../types'
import HardwareListRow from './HardwareListRow'
import HardwareSelectorListHeader, { HeaderPropsType } from './HardwareSelectorListHeader'

const getInitialHeaderSize = (hasEnabledDistributors, hiddenColumnGroups?, isAU?, isCustomerHeader?): number => {
  const headerList = ['Item', 'Brand', 'Size']
  const suppressPriceAndStocks = hiddenColumnGroups?.has('price_stock_distributor')

  if (isCustomerHeader) {
    headerList.push('Quantity Needed', 'Combined Rating')
  } else if (!hiddenColumnGroups?.has('rating')) {
    headerList.push('Rating')
  }

  if (hasEnabledDistributors && !suppressPriceAndStocks) {
    headerList.push('Stock')
    headerList.push('Available from', 'Price')
  }

  return headerList.length
}

const useStyles = makeOpenSolarStyles((theme: OpenSolarThemeType) => ({
  list: {
    boxSizing: 'border-box',
    width: '100%',
  },
  stickyHeaderWrapper: {
    background: '#ffffff',
    position: 'sticky',
    zIndex: 1,
    top: 0,
  },
  header: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 50,
    backgroundColor: theme.greyLight3,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '10px 10px 0 0',
  },
  body: {
    overflow: 'hidden',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: '0 0 10px 10px',
  },
  pagination: {},
}))

export const HardwareSelectorTableLayout = ({
  header,
  pagination,
  list,
}: {
  header: React.ReactNode
  pagination: React.ReactNode
  list: React.ReactNode
}) => {
  const classes = useStyles()
  return (
    <div className={classes.list}>
      <div className={classes.stickyHeaderWrapper}>
        <div className={classes.header}>{header}</div>
      </div>
      <div className={classes.body}>{list}</div>
      <div className={classes.pagination}>{pagination}</div>
    </div>
  )
}

interface HardwareSelectorListProps<T> {
  data: T[]
  Header?: React.FC<HeaderPropsType>
  isSelectDisabled?: boolean
  loading: boolean

  onSelect(record: T, costSource?: CostSource, price?: number): void

  Pagination: React.ReactNode
  Row?: React.FC<HardwareListRowProps<T>>
  total: number
  hiddenColumnGroups?: Set<string>
  noResults?: React.ReactNode
  skipProjectPermissionCheck?: boolean

  multiSelectMode?: boolean
  selectedQuantities?: Map<string, number>
  onQuantityChange?: (record: T, newQuantity: number) => void
}

const HardwareSelectorList = <T extends HardwareComponentType | InverterWizardRecordType>({
  data,
  Header = HardwareSelectorListHeader,
  isSelectDisabled = false,
  loading,
  onSelect,
  Pagination,
  Row = (HardwareListRow as unknown) as React.FC<HardwareListRowProps<T>>,
  noResults,
  total,
  hiddenColumnGroups,
  skipProjectPermissionCheck = false,
  multiSelectMode = false,
  selectedQuantities,
  onQuantityChange,
}: HardwareSelectorListProps<T>) => {
  const projectPermission = useSelector((state: RootState) =>
    permissionsSelectors.getProjectPermissionByKey(state, 'components', skipProjectPermissionCheck)
  )
  const isAdmin = useSelector(authSelectors.getIsAdmin)
  const isAccountManager = useSelector(
    (state: RootState) => !!authSelectors.getCurrentRole(state)?.is_account_manager
  )
  const allowCreate = skipProjectPermissionCheck
    ? isAdmin || isAccountManager
    : projectPermission?.allowCreate ?? false

  const hasEnabledDistributors = (useSelector(orgSelectors.getEnabledHardwareSuppliers)?.length ?? 0) > 0
  const isAU = useSelector(orgSelectors.getOrgIso2) === 'AU'
  const isCustomerHeader = Header === InverterAutoListHeader

  const List = useMemo(() => {
    if (loading) {
      return (
        <DatagridLoading
          hasBulkActions={false}
          nbChildren={getInitialHeaderSize(hasEnabledDistributors, hiddenColumnGroups, isAU, isCustomerHeader)}
          size="small"
        />
      )
    }
    if (total === 0 && !loading) {
      return noResults || <ListResultNotFound />
    }

    return (
      <div>
        {data.map((record, index) => (
          <Row
            allowCreate={allowCreate}
            isSelectDisabled={isSelectDisabled}
            record={record}
            onSelect={onSelect}
            key={index}
            hiddenColumnGroups={hiddenColumnGroups}
            multiSelectMode={multiSelectMode}
            selectedQuantity={selectedQuantities?.get((record as HardwareComponentType).code)}
            onQuantityChange={onQuantityChange}
          />
        ))}
      </div>
    )
  }, [allowCreate, isSelectDisabled, loading, total, data, multiSelectMode, selectedQuantities, onQuantityChange])

  return (
    <HardwareSelectorTableLayout
      header={<Header hiddenColumnGroups={hiddenColumnGroups} />}
      list={List}
      pagination={Pagination}
    />
  )
}

export default HardwareSelectorList
